import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { getCoverageColor, getGradeColor, getSectorColor, getValidationColor, PILLAR_CONFIG } from '@/lib/constants';
import { LineChart, Line, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis } from 'recharts';
import { ExternalLink, GitCompareArrows, MapPinned, ShieldCheck, TrendingUp, X } from 'lucide-react';

export default function CompanyDetailModal({ company, onClose, onCompare }) {
  if (!company) return null;

  const gradeColor = getGradeColor(company.grade);
  const sectorColor = getSectorColor(company.sector);
  const validationColor = getValidationColor(company.validation_status);
  const coverageColor = getCoverageColor(company.coverage_level);

  return (
    <Dialog open={!!company} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto border-white/10 bg-[#0D2238] text-white" data-testid="company-detail-modal">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="border-white/10 text-[11px]" style={{ color: sectorColor, borderColor: `${sectorColor}40` }}>
                  {company.sector}
                </Badge>
                <Badge className="border border-white/10 text-[11px]" style={{ backgroundColor: `${coverageColor}24`, color: coverageColor }}>
                  {company.coverage_level}
                </Badge>
                <Badge className="border border-white/10 text-[11px]" style={{ backgroundColor: `${validationColor}24`, color: validationColor }}>
                  {company.validation_status.replace('_', ' ')}
                </Badge>
              </div>
              <DialogTitle className="text-2xl font-bold tracking-tight">{company.name}</DialogTitle>
              <p className="mt-2 text-sm text-white/50">
                HQ {company.hq_country} &middot; GP market {company.gp_country} &middot; {company.sub_sector}
              </p>
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={onClose} data-testid="company-detail-close" className="text-white/60 hover:bg-white/[0.08] hover:text-white">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <p className="font-mono text-5xl font-bold tabular-nums" style={{ color: gradeColor }}>
                  {company.overall_score}
                </p>
                <p className="mt-1 text-sm text-white/45">Country rank #{company.country_rank} / {company.country_list_size}</p>
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2 text-sm text-white/65">
                  <TrendingUp className="h-4 w-4 text-[#4ECDC4]" />
                  <span className="font-mono tabular-nums text-white">{company.improvement_pct > 0 ? '+' : ''}{company.improvement_pct}%</span>
                  <span>{company.improvement_trajectory}</span>
                </div>
                <p className="text-sm text-white/55">{company.coverage_note}</p>
              </div>
              <Button
                type="button"
                onClick={() => onCompare(company.id)}
                className="rounded-2xl bg-[#2A9D8F] text-white hover:bg-[#24897d]"
                data-testid="company-detail-compare-button"
              >
                <GitCompareArrows className="mr-2 h-4 w-4" />
                Add to compare
              </Button>
            </div>

            {company.historical_scores?.length ? (
              <div className="mt-6 h-56 rounded-2xl border border-white/10 bg-[#0B1E31] p-3">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={company.historical_scores}>
                    <XAxis dataKey="date" stroke="rgba(231,237,245,0.28)" tick={{ fontSize: 11, fill: 'rgba(231,237,245,0.45)' }} />
                    <YAxis stroke="rgba(231,237,245,0.28)" tick={{ fontSize: 11, fill: 'rgba(231,237,245,0.45)' }} />
                    <ReTooltip contentStyle={{ backgroundColor: '#0D2238', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12 }} />
                    <Line type="monotone" dataKey="overall_score" stroke="#4ECDC4" strokeWidth={2.5} dot={false} name="Overall" />
                    <Line type="monotone" dataKey="risk_score" stroke="rgba(255,107,107,0.45)" strokeWidth={1.4} dot={false} name="Risk" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : null}
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/35">
              <MapPinned className="h-4 w-4 text-[#3498DB]" />
              Coverage telemetry
            </div>
            <div className="mt-4 space-y-3 text-sm text-white/60">
              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                <span>GP country</span>
                <span className="font-medium text-white">{company.gp_country}</span>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                <span>HQ country</span>
                <span className="font-medium text-white">{company.hq_country}</span>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                <span>Verification</span>
                <span className="font-medium text-white">{company.verification_status}</span>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                <span>Data source</span>
                <span className="font-medium text-white">{company.data_source}</span>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                <span>Disclosure date</span>
                <span className="font-medium text-white">{company.disclosure_date}</span>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                <span>Confidence</span>
                <span className="font-medium text-white">{company.confidence_level}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
            <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-white/45">Pillar breakdown</h3>
            <div className="mt-4 space-y-4">
              {PILLAR_CONFIG.map((pillar) => (
                <div key={pillar.key}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-white/70">{pillar.name}</span>
                    <span className="font-mono tabular-nums" style={{ color: pillar.color }}>{company[pillar.key]}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-white/[0.06]">
                    <div className="h-full rounded-full" style={{ width: `${company[pillar.key]}%`, backgroundColor: pillar.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
            <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-white/45">Key strengths & improvements</h3>
            <div className="mt-4 space-y-4">
              <div>
                <p className="mb-2 text-xs uppercase tracking-[0.16em] text-white/30">Strengths</p>
                <ul className="space-y-2 text-sm text-white/68">
                  {company.key_strengths?.map((strength) => (
                    <li key={strength}>• {strength}</li>
                  ))}
                </ul>
              </div>
              <Separator className="bg-white/10" />
              <div>
                <p className="mb-2 text-xs uppercase tracking-[0.16em] text-white/30">Recent improvements</p>
                <ul className="space-y-2 text-sm text-white/68">
                  {company.recent_improvements?.map((item) => (
                    <li key={item}>• {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-white/45">
              <ShieldCheck className="h-4 w-4 text-[#2A9D8F]" />
              Source documents
            </div>
            <div className="space-y-3">
              {company.source_documents?.map((document) => (
                <div key={`${document.title}-${document.disclosure_date}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <p className="font-medium text-white">{document.title}</p>
                  <p className="mt-1 text-sm text-white/50">{document.source_type} &middot; {document.disclosure_date} &middot; {document.verification_status}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-white/45">
              <ExternalLink className="h-4 w-4 text-[#3498DB]" />
              Third-party references
            </div>
            <div className="space-y-3">
              {company.third_party_refs?.map((reference) => (
                <div key={`${reference.provider}-${reference.reference}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <p className="font-medium text-white">{reference.provider}</p>
                  <p className="mt-1 text-sm text-white/50">{reference.reference}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {company.sector_metrics && Object.keys(company.sector_metrics).length ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
            <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-white/45">Sector metrics</h3>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {Object.entries(company.sector_metrics).map(([key, value]) => (
                <div key={key} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-white/35">{key}</p>
                  <p className="mt-2 font-mono text-sm text-white">{value}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
